import { TrendingUp, Trophy } from "lucide-react";
import { PARTNERS } from "@/config/partners";

// Etiqueta amigable: usa el nombre real del partner desde PARTNERS si aplica.
const originLabel = (o) => {
  if (o.partner) {
    const found = PARTNERS.find(
      (p) => p.code.toLowerCase() === (o.partner || "").toLowerCase()
    );
    if (found) return found.name;
    const p = o.partner.toLowerCase();
    return p.charAt(0).toUpperCase() + p.slice(1);
  }
  if (o.ref) return `Ref: ${o.ref}`;
  return "Sitio general";
};

const originId = (o) =>
  o.partner || (o.ref ? `ref:${o.ref}` : "sitio_general");

const badgeCls = (isPartner, isRef) => {
  if (isPartner) return "bg-brand-yellow/20 text-brand-ink";
  if (isRef) return "bg-indigo-100 text-indigo-700";
  return "bg-slate-100 text-slate-700";
};

// Widget compacto que compara la conversión (Leads → Emitidos) por cada origen.
// Muestra: nombre · leads · emitidos · % conversión + barra visual.
// El mejor origen (mayor %) queda destacado con un trofeo.
export const ConversionByOrigin = ({ origenes }) => {
  if (!origenes || origenes.length === 0) return null;

  // Ordenamos por leads (volumen) desc para que los orígenes con más tráfico
  // aparezcan primero — el mejor % se resalta con el trofeo aparte.
  const items = origenes
    .map((o) => {
      const count = Number(o.count || 0);
      const won = Number(o.won || 0);
      const rate = count > 0 ? Math.round((won / count) * 100) : 0;
      return { ...o, count, won, rate, id: originId(o) };
    })
    .sort((a, b) => b.count - a.count);

  // Detectamos el líder de conversión sólo entre orígenes con al menos 1 lead.
  const withLeads = items.filter((i) => i.count > 0);
  const topId =
    withLeads.length > 0
      ? withLeads.reduce((best, cur) =>
          cur.rate > best.rate ? cur : best
        , withLeads[0]).id
      : null;

  return (
    <section
      data-testid="conversion-by-origin"
      className="mt-6 rounded-2xl border border-gray-200 bg-white p-5 shadow-sm"
    >
      <header className="mb-4 flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-2">
          <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-emerald-100 text-emerald-700">
            <TrendingUp className="h-4 w-4" />
          </div>
          <div>
            <h3 className="font-display text-base font-bold text-brand-ink leading-tight">
              Conversión por origen
            </h3>
            <p className="text-[11px] text-gray-500 leading-tight">
              Leads → Emitidos (%) comparando el sitio general con partners co-brandeados
            </p>
          </div>
        </div>
      </header>

      <ul className="divide-y divide-gray-100">
        {items.map((o) => {
          const isTop = o.id === topId && o.count > 0;
          const isPartner = !!o.partner;
          const isRef = !!o.ref && !o.partner;
          return (
            <li
              key={o.id}
              data-testid={`conversion-row-${o.id}`}
              className={`py-3 first:pt-0 last:pb-0 ${isTop ? "" : ""}`}
            >
              <div className="flex items-center justify-between gap-3 flex-wrap sm:flex-nowrap">
                <div className="min-w-0 flex items-center gap-2">
                  <span
                    className={`inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide ${badgeCls(
                      isPartner,
                      isRef
                    )}`}
                  >
                    {isPartner ? "Partner" : isRef ? "Afiliado" : "General"}
                  </span>
                  <span className="text-sm font-semibold text-brand-ink truncate">
                    {originLabel(o)}
                  </span>
                  {isTop && (
                    <span
                      data-testid={`conversion-top-badge-${o.id}`}
                      className="inline-flex items-center gap-1 rounded-full bg-emerald-100 text-emerald-700 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide"
                      title="Mejor tasa de conversión"
                    >
                      <Trophy className="h-3 w-3" />
                      Líder
                    </span>
                  )}
                </div>
                <div className="flex items-center gap-4 shrink-0">
                  <span className="text-xs text-gray-500 tabular-nums">
                    <span className="font-bold text-brand-ink">{o.won}</span>
                    <span className="text-gray-400"> / </span>
                    <span>{o.count}</span>
                    <span className="ml-1 text-gray-400">leads</span>
                  </span>
                  <span
                    data-testid={`conversion-rate-${o.id}`}
                    className={`inline-flex items-center justify-center min-w-[54px] rounded-full px-2.5 py-1 text-sm font-bold tabular-nums ${
                      o.rate >= 30
                        ? "bg-emerald-100 text-emerald-700"
                        : o.rate >= 15
                        ? "bg-amber-100 text-amber-700"
                        : "bg-gray-100 text-gray-600"
                    }`}
                  >
                    {o.rate}%
                  </span>
                </div>
              </div>
              <div className="mt-2 h-1.5 w-full overflow-hidden rounded-full bg-gray-100">
                <div
                  className={`h-full rounded-full transition-all ${
                    o.rate >= 30
                      ? "bg-emerald-500"
                      : o.rate >= 15
                      ? "bg-amber-500"
                      : "bg-gray-400"
                  }`}
                  style={{ width: `${Math.min(100, Math.max(3, o.rate))}%` }}
                />
              </div>
            </li>
          );
        })}
      </ul>
    </section>
  );
};
